import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Popover, PopoverBody, PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useBreakpointValue,
  useToast
} from "@chakra-ui/react";
import { FaChevronRight, FaComment } from "react-icons/fa";
import { FormCheckBox, FormInput, FormTextArea } from "../../../../components/Forms";
import { Card } from "../../../../components/Card";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useGetAllCSRDAnswersByQuestionId } from "../../../../hooks/query/csrd";
import { useState } from "react";
import { QuestionIconTooltip } from "../../../../components";

function CSRDQuestion({
                        questionEl,
                        questionElIndex,
                        questionnaireYear,
                        register,
                        control,
                        setValue,
                        isPreview
                      }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "csrd.form"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();

  const notApplicableValue = useWatch({
    control,
    name: `${questionEl.id}.notApplicable`
  });
  const isCompleted = useWatch({
    control,
    name: `${questionEl.id}.status`
  });
  const answerValue = useWatch({
    control,
    name: `${questionEl.id}.answer`
  });
  const sourceValue = useWatch({
    control,
    name: `${questionEl.id}.source`
  });

  const handleNotApplicableCheckboxChange = (isChecked) => {
    setValue(`${questionEl.id}.answer`, isChecked ? "n/a" : "");
    setValue(`${questionEl.id}.source`, isChecked ? "n/a" : "");
  };

  const [isOpen, setIsOpen] = useState(false);

  const {
    data: allAnswersData
  } = useGetAllCSRDAnswersByQuestionId({
    questionId: questionEl.id,
    year: questionnaireYear
  }, {
    enabled: isOpen,
    cacheTime: 0,
    refetchOnWindowFocus: false
  });

  const buttonsFlexDir = useBreakpointValue({
    base: "column",
    "2xl": "row"
  });

  const [isAnswerFocused, setIsAnswerFocused] = useState(false);
  const [isCommentFocused, setIsCommentFocused] = useState(false);

  return (
    <Card width={"100%"} padding={2} marginBlockStart={questionElIndex === 0 ? 0 : 5}>
      <Flex flexFlow={"column"} gap={2}>

        <Flex alignItems={"center"} justifyContent={"space-between"} gap={2}>
          <Flex maxWidth={"80%"} alignItems={"center"}>
            <Text fontWeight="600" backgroundColor={"esg_active"} borderRadius={20} padding={1}>
              Q{questionElIndex + 1}
            </Text>
            <Divider borderColor="#A0AEC0" orientation="vertical" height={"28px"} marginInline={2} />
            {questionEl.subTopic !== null && typeof questionEl.subTopic !== "undefined" && questionEl.subTopic !== "" ? (
              <Flex alignItems={"center"}>

                <Breadcrumb color={"#A0AEC0"} spacing="8px" separator={<FaChevronRight color="#A0AEC0" />}>
                  <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink>{questionEl.subTopic}</BreadcrumbLink>
                  </BreadcrumbItem>
                  {questionEl.subSubTopic !== null && typeof questionEl.subSubTopic !== "undefined" &&
                  questionEl.subSubTopic !== "" ?
                    <BreadcrumbItem isCurrentPage>
                      <BreadcrumbLink>{questionEl.subSubTopic}</BreadcrumbLink>
                    </BreadcrumbItem> : null
                  }
                </Breadcrumb>
              </Flex>) : null
            }
          </Flex>

          <Flex gap={2} flexDir={buttonsFlexDir} alignItems={"center"}>
            <FormCheckBox
              label={t("completed")}
              {...register(`${questionEl.id}.status`)}
              control={control}
              display={"flex"}
              isDisabled={isPreview || notApplicableValue === true}
              size={"sm"}
              controlProps={{
                width: "115px",
                onChange: (e) => {
                  if (e.target.checked && (answerValue === "" || sourceValue === "")) {
                    // If any of answer or source value is empty, then the checkbox can't be set to true (completed)
                    setValue(`${questionEl.id}.status`, false);
                    if (!toast.isActive("emptyFields")) {
                      toast({
                        id: "emptyFields",
                        title: t("requiredFieldsWarning"),
                        position: "top-right",
                        isClosable: true,
                        status: "warning"
                      })
                    }
                  }
                }
              }}
            />
            <FormCheckBox
              label={t("notApplicable")}
              {...register(`${questionEl.id}.notApplicable`)}
              control={control}
              display={"flex"}
              isDisabled={isPreview || isCompleted}
              controlProps={{
                onClick: (e) => {
                  if (typeof e.target?.checked !== "undefined") {
                    handleNotApplicableCheckboxChange(e.target?.checked);
                  }
                },
                width: "115px"
              }}
              size={"sm"}
            />
            <Popover
              defaultIsOpen={false}
              placement={"bottom-end"}
              closeOnBlur={true}
              trigger="click"
              gutter={0}
              onClose={() => setIsOpen(false)}
            >
              <PopoverTrigger>
                <Button
                  variant="ghost"
                  display="flex"
                  alignItems="center"
                  width={"140px"}
                  fontSize={"sm"}
                  onClick={() => {
                    setIsOpen(true);
                  }}
                  _focus={{
                    outline: "none",
                    textDecoration: "underline"
                  }}
                >
                  <IconButton
                    aria-label={"viewAnswersButton"}
                    as="div"
                    variant="outline"
                    borderRadius="full"
                    size="sm"
                    tabIndex={-1}
                    pointerEvents="none"
                    flexShrink="0"
                    icon={<FaComment size="14" />}
                  />
                  <Text marginInlineStart="2">{t("viewAnswers")}</Text>
                </Button>
              </PopoverTrigger>

              <PopoverContent color="black" bg="white" borderColor="black" minWidth={"450px"} fontSize={"sm"}>
                <PopoverHeader fontWeight="bold" border="0">
                  {t("allAnswers")}
                </PopoverHeader>

                <PopoverCloseButton />

                <PopoverBody
                  height={!allAnswersData || allAnswersData?.length === 0 ? "100px" : "250px"}
                  overflow="auto"
                >
                  {allAnswersData !== null && typeof allAnswersData !== "undefined" && allAnswersData.length > 0 ?
                    allAnswersData.map((el, index) => {
                      return <Box key={`${questionEl.id}-answer-${index}`}>
                        <Box>
                          <Text fontWeight={"bold"} as="span">
                            {t("user")}
                          </Text>:
                          <Text marginInlineStart={1} as="span">
                            {el.userName}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontWeight={"bold"} as="span">
                            {t("answer")}
                          </Text>:
                          <Text marginInlineStart={1} as="span">
                            {el.answer}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontWeight={"bold"} as="span">
                            {t("dataSource")}
                          </Text>:
                          <Text marginInlineStart={1} as="span">
                            {el.dataSource}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontWeight={"bold"} as="span">
                            {t("comment")}
                          </Text>:
                          <Text marginInlineStart={1} as="span">
                            {el.comment}
                          </Text>
                        </Box>

                        {index < allAnswersData.length - 1 ?
                          <Divider marginBlock={2} /> : null}
                      </Box>;
                    }) :
                    <Text fontSize="md" fontWeight="bold">{globalTranslation("noDataAvailable")}</Text>
                  }
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        </Flex>

        <Text fontWeight="500" fontSize="md" whiteSpace="pre-wrap">
          {questionEl.question.replace(/\\n/g, "\n")}
        </Text>

        <Grid templateColumns="repeat(2, 1fr)" gap={2} marginBottom={2}>
          <GridItem>
            <FormTextArea
              name={"yourAnswer"}
              label={
                <Flex alignItems="top">
                  {`${t("yourAnswer")} ${notApplicableValue === true ? "" : "*"}`}
                  <QuestionIconTooltip
                    label={`${t("expectedAnswerType")}: ${questionEl.dataType}`}
                    ariaLabel={"answer-tooltip"}
                    height={"unset"}
                    fontSize={"sm"}
                  />
                </Flex>
              }
              {...register(`${questionEl.id}.answer`)}
              disabled={notApplicableValue === true}
              controlProps={{ height: "100%" }}
              onFocus={() => setIsAnswerFocused(true)}
              onBlur={() => setIsAnswerFocused(false)}
              maxHeight={notApplicableValue === true ? "40px" : `calc(100% - ${isAnswerFocused ? 25 : 65}px)`}
              minHeight={notApplicableValue === true ? "40px" : `calc(100% - ${isAnswerFocused ? 25 : 65}px)`}
              fontSize={"sm"}
              readOnly={isPreview || isCompleted ? true : null}
              opacity={isPreview || isCompleted ? 0.4 : 1}
              title={isCompleted && !isPreview ? t("completedFieldTooltip") : ""}
            />
          </GridItem>
          <GridItem>
            <FormInput
              label={
                <Flex alignItems="top">
                  {`${t("dataSource")} ${notApplicableValue === true ? "" : "*"}`}
                  <QuestionIconTooltip
                    label={t("dataSourceTooltip")}
                    ariaLabel={"data-source-tooltip"}
                    height={"unset"}
                    fontSize={"sm"}
                  />
                </Flex>
              }
              {...register(`${questionEl.id}.source`)}
              disabled={notApplicableValue === true}
              width={"100%"}
              maxInlineSize={"full"}
              fontSize={"sm"}
              readOnly={isPreview || isCompleted ? true : null}
              opacity={isPreview || isCompleted ? 0.4 : 1}
              title={isCompleted && !isPreview ? t("completedFieldTooltip") : ""}
            />
            <FormTextArea
              label={t("yourComment")}
              {...register(`${questionEl.id}.comment`)}
              controlProps={{ height: "100%", marginTop: "10px" }}
              maxHeight={isCommentFocused === true ? "100px" : "30px"}
              minHeight={isCommentFocused === true ? "100px" : "30px"}
              onFocus={() => setIsCommentFocused(true)}
              onBlur={() => setIsCommentFocused(false)}
              fontSize={"sm"}
              readOnly={isPreview || isCompleted ? true : null}
              opacity={isPreview || isCompleted ? 0.4 : 1}
              title={isCompleted && !isPreview ? t("completedFieldTooltip") : ""}
            />
          </GridItem>
        </Grid>
      </Flex>
    </Card>
  );
}

export default CSRDQuestion;